import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type Resolver, useForm, useWatch } from 'react-hook-form';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiSend } from 'react-icons/bi';
import { isValidPhoneNumber } from 'react-phone-number-input';
import z from 'zod';

import { Stepper } from '@/components';
import { Accordion } from '@/components/Accordion/Accordion';
import { Button, ButtonOutlined } from '@/components/Button';
import DateTimePicker from '@/components/DateTimePicker/DateTimePicker';
import { ContactInput, Input, Select, TextArea, Toggle } from '@/components/Inputs';
import { INVALID_CONTACT_MESSAGE } from '@/components/Inputs/contactMessages';
import inputStyles from '@/components/Inputs/inputs.module.scss';
import PermissionRow from '@/pages/PermissionSettingsPage/components/PermissionRow/PermissionRow';
import {
  CATEGORY_META,
  getPermissionMeta,
  hasPermissionMeta,
} from '@/pages/PermissionSettingsPage/permissions.ui';
import { canGrant, OWNER_ONLY_PERMISSIONS } from '@/permissions/grantable';
import { buildPermissionGates } from '@/permissions/permission-gates';
import {
  PERMISSION_CATEGORIES,
  type PermissionCategory,
  PERMISSIONS,
} from '@/permissions/permissions.catalog';
import { applyPermissionRules } from '@/permissions/permissions.rules';
import { usePermissions as useViewerPermissions } from '@/permissions/usePermissions';
import { useAuth } from '@/store/auth/auth.store';
import {
  contractEndAt,
  contractStartAt,
  contractTimezoneNote,
  temporaryHireAllowed,
} from '@/store/contracts/contracts.mappers';
import { seatAvailabilityWarning } from '@/store/invites/invites.mappers';
import { useSeatAvailabilityQuery } from '@/store/invites/invites.store';
import { getServiceRolePermissions } from '@/store/permissions/permissions.api';
import { usePermissions, usePlanSeatQuery } from '@/store/permissions/permissions.store';
import type { Vessel } from '@/store/vessels/vessel.dto';
import { UserRole } from '@/utils/roles';
import {
  calendarDayToLocalDate,
  DEFAULT_TIME_ZONE,
  localDateToCalendarDay,
  todayInTimeZone,
} from '@/utils/timezone';

import s from './Crew.module.scss';

/**
 * Hiring someone for a set number of days: who and for which role, what to tell them, and what
 * they may do aboard. Used by both crew invitations — by email or phone, and from the crew search.
 */

const STEPS = ['Details', 'Message', 'Permissions'] as const;
type Step = 0 | 1 | 2;

export interface TemporaryHire {
  name: string;
  contact: string;
  role: string;
  /** ISO, UTC: the vessel's midnight on the first day picked. */
  startAt: string;
  /** ISO, UTC: the vessel's midnight after the last day picked. */
  endAt: string;
  message?: string;
  /** Only what the hiring user may grant and the vessel's plan allows. */
  permissions: string[];
}

interface TemporaryHireWizardProps {
  vessel: Pick<Vessel, 'id' | 'vessel_name' | 'timezone'>;
  /** `external` asks who to invite; `internal` hires the candidate already chosen. */
  mode: 'external' | 'internal';
  /** The candidate's name, for an `internal` hire. */
  candidateName?: string;
  /** A role settled before the wizard opened; the picker is then left out. */
  fixedRole?: string;
  onSubmit(hire: TemporaryHire): Promise<void>;
  /** Told the step the wizard moved to, for a caller that shows something beside one step only. */
  onStepChange?(step: number): void;
}

const contactSchema = z
  .string()
  .email(INVALID_CONTACT_MESSAGE)
  .or(z.string().refine((value) => isValidPhoneNumber(value), INVALID_CONTACT_MESSAGE));

const detailsSchema = (mode: TemporaryHireWizardProps['mode'], today: string) =>
  z
    .object({
      name: mode === 'external' ? z.string().min(1, 'Please enter a name.') : z.string(),
      contact: mode === 'external' ? contactSchema : z.string(),
      role: z.string('Please enter a job title.').min(1, 'Please enter a job title.'),
      startDate: z
        .string('Pick the first day')
        .min(1, 'Pick the first day')
        .refine((day) => day >= today, "The contract can't start before today (vessel time)"),
      endDate: z.string('Pick the last day').min(1, 'Pick the last day'),
    })
    .refine(({ startDate, endDate }) => !startDate || !endDate || endDate >= startDate, {
      path: ['endDate'],
      message: "The last day can't be before the first",
    });

const messageSchema = z.object({
  message: z.string().max(500, 'Keep the message under 500 characters').optional(),
});

type HireForm = {
  name: string;
  contact: string;
  role: string;
  startDate: string;
  endDate: string;
  message?: string;
};

/** Everything below starts switched off: money and the rows marked sensitive. */
const OFF_BY_DEFAULT_CATEGORY = 'wallet';

/** Not offered: the role's permissions in these always go with the hire. */
const ROLE_CATEGORIES: ReadonlySet<PermissionCategory> = new Set([
  'vessel',
  'communication',
  'service-provider',
]);

const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1).replace(/-/g, ' ');

export const TemporaryHireWizard = ({
  vessel,
  mode,
  candidateName = '',
  fixedRole,
  onSubmit,
  onStepChange,
}: TemporaryHireWizardProps) => {
  const timeZone = vessel.timezone || DEFAULT_TIME_ZONE;
  const today = todayInTimeZone(timeZone);

  const [step, setStep] = useState<Step>(0);
  const [furthest, setFurthest] = useState<Step>(0);
  // Which schema the resolver applies; moved with the step, in the handler that moves it.
  const stepRef = useRef<Step>(0);

  const schemas = useMemo(
    () => [detailsSchema(mode, today), messageSchema, z.object({})] as const,
    [mode, today],
  );
  // Each step answers to its own schema: moving on validates only what that step asked.
  const resolver: Resolver<HireForm> = (values, context, options) =>
    (zodResolver(schemas[stepRef.current]) as Resolver<HireForm>)(values, context, options);

  const {
    control,
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<HireForm>({
    resolver,
    defaultValues: {
      name: candidateName,
      contact: '',
      role: fixedRole ?? '',
      startDate: '',
      endDate: '',
      message: '',
    },
  });

  const role = useWatch({ control, name: 'role' });
  const { data: seat } = useSeatAvailabilityQuery(vessel.id, role);
  const seatWarning = seatAvailabilityWarning(seat, true);
  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });

  /* ── Who is hiring, and what they may hand out ── */

  const myRole = useAuth(
    (state) => state.session?.services?.find((x) => x.service_id === vessel.id)?.role ?? '',
  );
  const { can, rights: myRights } = useViewerPermissions();
  const actingCaptain = myRole === UserRole.CAPTAIN && can('TransferVessel');

  const seatPermissions = usePermissions((state) => state.seatPermissions);
  const basePermissions = usePermissions((state) => state.basePermissions);
  const { fetchSeatPermissions, fetchBasePermissions } = usePermissions();
  const { data: plan, isPending: planPending } = usePlanSeatQuery(vessel.id);

  const [permissionListsLoaded, setPermissionListsLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const loaded = usePermissions.getState();

    Promise.all([
      loaded.seatPermissions.length === 0 ? fetchSeatPermissions() : undefined,
      loaded.basePermissions.length === 0 ? fetchBasePermissions() : undefined,
    ])
      .catch((err: unknown) => console.error(err))
      .finally(() => {
        if (active) setPermissionListsLoaded(true);
      });

    return () => {
      active = false;
    };
  }, [fetchSeatPermissions, fetchBasePermissions]);

  const { data: serviceRoles = [], isPending: rolesPending } = useQuery({
    queryKey: ['service-roles', vessel.id],
    queryFn: () => getServiceRolePermissions(vessel.id),
    enabled: !!vessel.id,
  });

  const roleOptions = serviceRoles
    .filter(({ role: option }) => temporaryHireAllowed(option, myRole))
    .map(({ role: option }) => ({ label: roleLabel(option), value: option }));

  const granter = useMemo(
    () => ({ myRole, myRights, actingCaptain }),
    [myRole, myRights, actingCaptain],
  );

  /**
   * The rows this step offers: what the hiring user may grant and what the vessel's plan
   * allows, outside the role's categories. Anything else is not shown at all, owner-only rights
   * included, and never sent.
   */
  const visibleByCategory = useMemo(() => {
    const gates = buildPermissionGates(seatPermissions, plan?.seat ?? null, plan?.seat ?? null);
    const base = new Set(basePermissions);

    return PERMISSION_CATEGORIES.filter((category) => !ROLE_CATEGORIES.has(category))
      .map((category) => ({
        category,
        names: Object.values(PERMISSIONS[category] as Record<string, string>).filter(
          (name) =>
            hasPermissionMeta(name) &&
            !base.has(name) &&
            !OWNER_ONLY_PERMISSIONS.has(name) &&
            !gates.reasonFor(name) &&
            canGrant(name, granter),
        ),
      }))
      .filter(({ names }) => names.length > 0);
  }, [seatPermissions, plan?.seat, basePermissions, granter]);

  const visible = useMemo(
    () => new Set(visibleByCategory.flatMap(({ names }) => names)),
    [visibleByCategory],
  );

  const rolePermissions = useMemo(
    () => serviceRoles.find(({ role: option }) => option === role)?.permissions ?? [],
    [serviceRoles, role],
  );

  /**
   * The role's categories are not offered: the member always gets the role's permissions in
   * them, sensitive and owner-only ones aside, as far as the hiring user may hand them
   * out. Anything the plan does not cover the service drops on its own.
   */
  const rolePart = useMemo(() => {
    const copied = new Set<string>(
      [...ROLE_CATEGORIES].flatMap((category) => Object.values(PERMISSIONS[category])),
    );

    return rolePermissions.filter(
      (name) =>
        copied.has(name) &&
        !getPermissionMeta(name).sensitive &&
        !OWNER_ONLY_PERMISSIONS.has(name) &&
        canGrant(name, granter),
    );
  }, [rolePermissions, granter]);

  /**
   * Where the offered rows start: the role's own, sensitive ones and the whole wallet off. Worked
   * out from what has loaded so far, so a late plan or seat answer still lands in it.
   */
  const defaults = useMemo(() => {
    const wallet = new Set<string>(Object.values(PERMISSIONS[OFF_BY_DEFAULT_CATEGORY]));

    return new Set(
      rolePermissions.filter(
        (name) => visible.has(name) && !getPermissionMeta(name).sensitive && !wallet.has(name),
      ),
    );
  }, [rolePermissions, visible]);

  // Until every answer the rows depend on is in, the step shows nothing it could later take back.
  const permissionsReady = !rolesPending && !planPending && permissionListsLoaded;

  /* ── The permission set ── */

  // The user's own choice, for the role it was made on; until then the defaults stand.
  const [edited, setEdited] = useState<{ role: string; names: Set<string> } | null>(null);
  const selected = edited?.role === role ? edited.names : defaults;

  const isBlocked = (name: string) => !visible.has(name);

  const toggle = (names: string[], enabled: boolean) => {
    const next = new Set(selected);
    for (const name of names) {
      if (enabled) next.add(name);
      else next.delete(name);
      applyPermissionRules(next, name, enabled, isBlocked);
    }
    setEdited({ role, names: next });
  };

  // Filtered once more here: nothing hidden goes out, whatever the state says. The role's
  // permissions in its categories always go with it. An empty set would hand the hire the role's defaults instead.
  const permissionsToSend = [
    ...new Set([...rolePart, ...[...selected].filter((name) => visible.has(name))]),
  ];

  /* ── Moving through the steps ── */

  const goTo = (next: Step) => {
    stepRef.current = next;
    setStep(next);
    setFurthest((current) => (next > current ? next : current));
    onStepChange?.(next);
  };

  const next = async () => {
    if (!(await trigger())) return;
    goTo((step + 1) as Step);
  };

  const sendHire = async () => {
    // The resolver only parses the last step, whose schema keeps no fields, so the answers are
    // read from the form itself.
    const values = getValues();
    // Earlier answers may have been changed after going back; send nothing they no longer pass.
    const failed = ([0, 1] as const).find((index) => !schemas[index].safeParse(values).success);
    if (failed !== undefined) {
      goTo(failed);
      await trigger();
      return;
    }

    if (permissionsToSend.length === 0) return;

    await onSubmit({
      name: values.name.trim(),
      contact: values.contact,
      role: values.role,
      // The days were picked on the vessel's calendar; the service only ever sees instants.
      startAt: contractStartAt(values.startDate, timeZone),
      endAt: contractEndAt(values.endDate, timeZone),
      message: values.message?.trim() || undefined,
      permissions: permissionsToSend,
    });
  };

  const pickerValue = (day?: string) => (day ? calendarDayToLocalDate(day).toISOString() : null);

  return (
    <form
      className={s.ModalContent}
      onSubmit={(event) => {
        event.preventDefault();
        if (step === 2) void handleSubmit(sendHire)();
        else void next();
      }}
    >
      <Stepper
        steps={STEPS}
        current={step}
        furthest={furthest}
        onStepClick={async (index) => {
          // Forward only past an answered step; back is always open.
          if (index > step && !(await trigger())) return;
          goTo(index as Step);
        }}
      />

      {step === 0 && (
        <>
          {mode === 'external' && (
            <>
              <Input
                label="Name"
                placeholder="How should we call them"
                error={errors.name?.message}
                {...register('name')}
                mandatory
              />
              <Controller
                name="contact"
                control={control}
                render={({ field }) => (
                  <ContactInput
                    label="Email or mobile number"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.contact?.message}
                    mandatory
                  />
                )}
              />
            </>
          )}
          {!fixedRole && (
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select
                  label="Job Title"
                  placeholder={rolesPending ? 'Loading job titles…' : 'Select Job Title'}
                  disabled={rolesPending}
                  mandatory
                  options={roleOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.role?.message}
                  warning={seatWarning}
                />
              )}
            />
          )}
          {fixedRole && seatWarning && (
            <p className={inputStyles.Warning}>
              <AiOutlineWarning size={14} aria-hidden="true" />
              {seatWarning}
            </p>
          )}
          <DateTimePicker
            label="Contract Dates"
            placeholder="Choose dates"
            showTimeInput={false}
            mandatory
            endRequired
            startLabel="From"
            endLabel="To"
            value={pickerValue(startDate)}
            endValue={pickerValue(endDate)}
            minDate={calendarDayToLocalDate(today)}
            onRangeChange={(start, end) => {
              setValue('startDate', start ? localDateToCalendarDay(start) : '', {
                shouldValidate: isSubmitted,
              });
              setValue('endDate', end ? localDateToCalendarDay(end) : '', {
                shouldValidate: isSubmitted,
              });
            }}
            error={errors.endDate?.message ?? errors.startDate?.message}
          />
          <p className={s.wizardNote}>{contractTimezoneNote(timeZone, endDate || undefined)}</p>
        </>
      )}

      {step === 1 && (
        <TextArea
          label="Message"
          placeholder="Add a short note to the invitation"
          rows={4}
          maxLength={500}
          error={errors.message?.message}
          {...register('message')}
        />
      )}

      {step === 2 && !permissionsReady && <p className={s.wizardNote}>Loading permissions…</p>}

      {step === 2 && permissionsReady && (
        <div>
          {visibleByCategory.map(({ category, names }) => {
            const enabled = names.filter((name) => selected.has(name)).length;
            const title = CATEGORY_META[category].title;

            return (
              <Accordion
                key={category}
                title={title}
                chevron="start"
                inset
                actions={
                  <>
                    <span className={s.wizardCount}>
                      {enabled}/{names.length}
                    </span>
                    <Toggle
                      id={`temporary-${category}`}
                      aria-label={`${title} permissions`}
                      checked={enabled > 0}
                      onChange={(event) => toggle(names, event.target.checked)}
                    />
                  </>
                }
              >
                {names.map((name) => (
                  <PermissionRow
                    key={name}
                    name={name}
                    enabled={selected.has(name)}
                    onChange={(on) => toggle([name], on)}
                  />
                ))}
              </Accordion>
            );
          })}
          <p className={s.wizardNote}>
            {permissionsToSend.length === 0
              ? 'Switch on at least one permission to send the invitation.'
              : 'You can change these permissions at any time on the Permissions page.'}
          </p>
        </div>
      )}

      <div className={s.wizardActions}>
        {step > 0 && (
          <ButtonOutlined type="button" onClick={() => goTo((step - 1) as Step)}>
            Back
          </ButtonOutlined>
        )}
        <Button
          type="submit"
          disabled={
            isSubmitting || (step === 2 && (!permissionsReady || permissionsToSend.length === 0))
          }
        >
          {step < 2 ? (
            'Next'
          ) : (
            <>
              {isSubmitting ? 'Sending…' : 'Send Invitation'} <BiSend size={20} />
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
